import { useClerk, useUser } from '@clerk/clerk-react'
import { useStatsigClient } from '@statsig/react-bindings'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  isLanePerformanceInsightsAccessEnabled,
  isLanePerformanceMenuVisible,
} from '../../pages/InsightsDashboard/lanePerformance/lanePerformanceConfig'
import { getImpersonationTargetOrgId } from '../../utility/impersonateClerkUser'
import {
  getOrgSwitchPartnerOrgId,
  isOrgSwitchTransitionActive,
  ORG_SWITCH_SESSION_KEY,
} from '../../utility/orgSwitchStorage'
import { RELEASE_GATE_V2_13_0 } from '../../utility/releaseStatsigGateConstants.js'
import {
  ADMIN_SETTINGS_FEATURE_GATE,
  TENANT_DASHBOARD_FEATURE_KEY,
  TENANTS_FEATURE_GATE,
} from '../../utility/tenantStatsigGateConstants.js'
import {
  checkBackendOnlyFeatureAccess,
  checkTenantFeatureAccess,
  checkUserFeatureAccess,
  getUserUagName,
  isTenantOwner,
  userOrganizationPermissions,
} from '../../utility/utility'
import Header from '../Header/Header'
import Loader from '../Loader/Loader'
import NoPermissionEmptyState from '../NoPermissionEmptyState/NoPermissionEmptyState'

/** Permission keys aligned with Header sidebar / user-menu visibility checks. */
const ROUTE_PERMISSION_KEYS = {
  COMMAND_CENTER: 'commandcenter',
  COMMAND_CENTER_MANAGE: 'commandcenter_manage',
  TEMP_RELEASE: 'temprelease',
  TEMP_RELEASE_MANAGE: 'temprelease_manage',
  ATHENA_SANDBOX: 'athena_sandbox',
  QUALIFICATION_SANDBOX: 'qualification_sandbox',
  INSIGHTS_COMMAND_CENTER: 'insights_commandcenter',
  INSIGHTS_LANE_PERFORMANCE: 'insights_laneperformance',
  INSIGHTS_TEMP_RELEASE: 'insights_temprelease',
  INSIGHTS_LANE_PERFORMANCE_FEEDBACK: 'insights_lane_performance_feedback',
  AUTOMATION_WORKFLOWS: 'automation_workflows',
  AUTOMATION_WORKFLOWS_MANAGE: 'automation_workflows_manage',
  PRODUCT_PROFILES: 'product_profiles',
  LANE_PROFILES: 'lane_profiles',
  TAGS: 'tags',
  LEGACY_MANAGE: 'legacy_manage',
  LEGACY_MANAGE_ORG: 'legacy_manage_org',
  TENANT_ONBOARDING: 'tenant_onboarding',
  ADMIN_ORG_SETTINGS: 'admin_org_settings',
  ADMIN_FEATURE_MANAGEMENT: 'admin_feature_management',
  API_CONFIG: 'api_config',
  TENANT_ORG_MANAGEMENT: 'tenant_org_management',
  HELP: 'help',
  USER_IMPERSONATION: 'user_impersonation',
  ORG_OWNER_SETTINGS: 'org_owner_settings',
  UAG_USER_SETTINGS: 'uag_user_settings',
}

const PERMISSIONS_POLL_MS = 100
const PERMISSIONS_WAIT_MS = 5000

const parseStored = (key) => {
  try {
    const raw = localStorage[key]
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const hasStoredPermissions = (uagEnhancements) => {
  if (uagEnhancements) {
    return Boolean(localStorage.getItem('userFeaturePermissions'))
  }
  const legacyPermissions = parseStored('featurePermissions')
  return Boolean(legacyPermissions && Object.keys(legacyPermissions).length > 0)
}

const hasValidStoredPermissions = (uagEnhancements, organizationId) => {
  if (!hasStoredPermissions(uagEnhancements)) {
    return false
  }

  const impersonationTargetOrgId = getImpersonationTargetOrgId()
  if (impersonationTargetOrgId) {
    return Boolean(organizationId) && impersonationTargetOrgId === organizationId
  }

  if (isOrgSwitchTransitionActive()) {
    const transitionOrgId =
      sessionStorage.getItem(ORG_SWITCH_SESSION_KEY) || getOrgSwitchPartnerOrgId()
    if (!transitionOrgId || !organizationId) {
      return false
    }
    return transitionOrgId === organizationId
  }

  return true
}

function evaluateRoutePermission(permissionKey, context) {
  const {
    uagEnhancements,
    featurePermissions = {},
    hasFetchedPermissions = {},
    dynamicConfig = {},
    isAdmin,
    orgUserImpersonationEnabled,
    checkGate,
    hasClerkOrgPermission,
  } = context

  const checkGateFn = typeof checkGate === 'function' ? checkGate : () => false
  const useEnhancedFeatureGates = checkGateFn('fe-822-enhance-feature-gates-use')

  switch (permissionKey) {
    case ROUTE_PERMISSION_KEYS.COMMAND_CENTER:
      return uagEnhancements
        ? checkUserFeatureAccess('commandcenter', ['read', 'manage'])
        : Boolean(hasFetchedPermissions?.commandcenter ?? featurePermissions?.commandcenter)

    case ROUTE_PERMISSION_KEYS.COMMAND_CENTER_MANAGE:
      return uagEnhancements
        ? checkUserFeatureAccess('commandcenter', 'manage')
        : Boolean(hasClerkOrgPermission?.('org:manage:shipments'))

    case ROUTE_PERMISSION_KEYS.TEMP_RELEASE:
      return uagEnhancements
        ? checkUserFeatureAccess('temprelease', ['read', 'manage', 'can_review'])
        : Boolean(hasFetchedPermissions?.temprelease ?? featurePermissions?.temprelease)

    case ROUTE_PERMISSION_KEYS.TEMP_RELEASE_MANAGE:
      return uagEnhancements
        ? checkUserFeatureAccess('temprelease', 'manage')
        : Boolean(hasClerkOrgPermission?.('org:manage:shipments'))

    case ROUTE_PERMISSION_KEYS.ATHENA_SANDBOX:
      return uagEnhancements
        ? checkBackendOnlyFeatureAccess('athena_sandbox')
        : checkGateFn('athena-sandbox-v1')

    case ROUTE_PERMISSION_KEYS.QUALIFICATION_SANDBOX:
      return uagEnhancements
        ? checkBackendOnlyFeatureAccess('qualification_sandbox')
        : checkGateFn(RELEASE_GATE_V2_13_0)

    case ROUTE_PERMISSION_KEYS.INSIGHTS_COMMAND_CENTER:
      return false

    case ROUTE_PERMISSION_KEYS.INSIGHTS_TEMP_RELEASE:
      return checkUserFeatureAccess('insights_temprelease', 'read')

    case ROUTE_PERMISSION_KEYS.INSIGHTS_LANE_PERFORMANCE:
      if (uagEnhancements) {
        return checkUserFeatureAccess('insights_laneperformance', 'read')
      }
      return isLanePerformanceMenuVisible(hasFetchedPermissions)

    case ROUTE_PERMISSION_KEYS.INSIGHTS_LANE_PERFORMANCE_FEEDBACK:
      return isLanePerformanceInsightsAccessEnabled(hasFetchedPermissions)

    case ROUTE_PERMISSION_KEYS.AUTOMATION_WORKFLOWS:
      return uagEnhancements
        ? checkUserFeatureAccess('automation_workflows', ['read', 'manage'])
        : Boolean(
            hasFetchedPermissions?.['automation-workflows'] ??
            featurePermissions?.['automation-workflows'],
          )

    case ROUTE_PERMISSION_KEYS.AUTOMATION_WORKFLOWS_MANAGE:
      return uagEnhancements
        ? checkUserFeatureAccess('automation_workflows', 'manage')
        : Boolean(hasClerkOrgPermission?.('org:automation_workflow:manage'))

    case ROUTE_PERMISSION_KEYS.PRODUCT_PROFILES:
      return uagEnhancements
        ? checkUserFeatureAccess('product_profiles', ['read', 'manage'])
        : Boolean(hasFetchedPermissions?.product_profiles ?? featurePermissions?.product_profiles)

    case ROUTE_PERMISSION_KEYS.LANE_PROFILES:
      return uagEnhancements
        ? checkUserFeatureAccess('lane_profiles', ['read', 'manage'])
        : Boolean(hasFetchedPermissions?.lane_profiles ?? featurePermissions?.lane_profiles)

    case ROUTE_PERMISSION_KEYS.TAGS:
      return uagEnhancements
        ? checkUserFeatureAccess('tags', ['read', 'manage'])
        : Boolean(hasFetchedPermissions?.tags ?? featurePermissions?.tags)

    case ROUTE_PERMISSION_KEYS.LEGACY_MANAGE:
      return !uagEnhancements

    case ROUTE_PERMISSION_KEYS.LEGACY_MANAGE_ORG:
      if (uagEnhancements) {
        return false
      }
      if (useEnhancedFeatureGates) {
        return Boolean(featurePermissions?.org_settings)
      }
      return true

    case ROUTE_PERMISSION_KEYS.TENANT_ONBOARDING:
      if (uagEnhancements) {
        return false
      }
      if (useEnhancedFeatureGates) {
        return (
          featurePermissions?.tenant_onboarding_steps &&
          Object.values(featurePermissions.tenant_onboarding_steps).includes(true)
        )
      }
      if (!isAdmin) {
        return false
      }
      return Boolean(
        hasFetchedPermissions?.[TENANT_DASHBOARD_FEATURE_KEY] && checkGateFn(TENANTS_FEATURE_GATE),
      )

    case ROUTE_PERMISSION_KEYS.ADMIN_ORG_SETTINGS:
      if (uagEnhancements) {
        return false
      }
      if (useEnhancedFeatureGates) {
        return Boolean(featurePermissions?.tenant_onboarding_steps?.manage_organization)
      }
      if (!isAdmin) {
        return false
      }
      return checkGateFn(ADMIN_SETTINGS_FEATURE_GATE)

    case ROUTE_PERMISSION_KEYS.ADMIN_FEATURE_MANAGEMENT:
      if (uagEnhancements) {
        return false
      }
      if (useEnhancedFeatureGates) {
        return Boolean(featurePermissions?.feature_flag_management)
      }
      if (!isAdmin) {
        return false
      }
      return checkGateFn(ADMIN_SETTINGS_FEATURE_GATE)

    case ROUTE_PERMISSION_KEYS.API_CONFIG:
      if (uagEnhancements) {
        return false
      }
      if (useEnhancedFeatureGates) {
        return Boolean(featurePermissions?.api_config)
      }
      if (!isAdmin) {
        return false
      }
      return Boolean(
        hasFetchedPermissions?.[TENANT_DASHBOARD_FEATURE_KEY] && checkGateFn(TENANTS_FEATURE_GATE),
      )

    case ROUTE_PERMISSION_KEYS.TENANT_ORG_MANAGEMENT:
      return uagEnhancements && isTenantOwner()

    case ROUTE_PERMISSION_KEYS.HELP:
      if (uagEnhancements) {
        return checkTenantFeatureAccess('help')
      }
      if (useEnhancedFeatureGates) {
        return Boolean(dynamicConfig?.help_page)
      }
      return !checkGateFn('fe-560-hide-help-page')

    case ROUTE_PERMISSION_KEYS.USER_IMPERSONATION:
      if (uagEnhancements) {
        return checkBackendOnlyFeatureAccess('user_impersonation')
      }
      if (useEnhancedFeatureGates) {
        return Boolean(featurePermissions?.user_impersonation)
      }
      return Boolean(orgUserImpersonationEnabled && isAdmin)

    case ROUTE_PERMISSION_KEYS.ORG_OWNER_SETTINGS:
      return uagEnhancements && getUserUagName() === 'owner'

    case ROUTE_PERMISSION_KEYS.UAG_USER_SETTINGS:
      return uagEnhancements

    default:
      return true
  }
}

function resolveInsightsRoutePermission(pathname) {
  const normalizedPath = pathname.toLowerCase()

  if (normalizedPath.includes('/insights/lane-performance-feedback')) {
    return ROUTE_PERMISSION_KEYS.INSIGHTS_LANE_PERFORMANCE_FEEDBACK
  }
  if (normalizedPath.includes('/insights/lane-performance')) {
    return ROUTE_PERMISSION_KEYS.INSIGHTS_LANE_PERFORMANCE
  }
  if (normalizedPath.includes('/insights/command-center')) {
    return ROUTE_PERMISSION_KEYS.INSIGHTS_COMMAND_CENTER
  }
  if (normalizedPath.includes('/insights/temperature-release')) {
    return ROUTE_PERMISSION_KEYS.INSIGHTS_TEMP_RELEASE
  }

  return null
}

function resolveManageRoutePermission(pathname) {
  const normalizedPath = pathname.toLowerCase()

  if (!normalizedPath.startsWith('/manage')) {
    return null
  }

  if (normalizedPath.includes('/manage/organization')) {
    return ROUTE_PERMISSION_KEYS.LEGACY_MANAGE_ORG
  }
  if (normalizedPath.includes('/manage/impersonation')) {
    return ROUTE_PERMISSION_KEYS.USER_IMPERSONATION
  }

  return ROUTE_PERMISSION_KEYS.LEGACY_MANAGE
}

function hasRoutePermission(permissionKey, context, pathname = '') {
  const resolvedKey =
    permissionKey === 'insights_dynamic'
      ? resolveInsightsRoutePermission(pathname)
      : permissionKey === 'manage_dynamic'
        ? resolveManageRoutePermission(pathname)
        : permissionKey

  if (!resolvedKey) {
    return true
  }

  if (resolvedKey === ROUTE_PERMISSION_KEYS.LEGACY_MANAGE_ORG) {
    return (
      evaluateRoutePermission(ROUTE_PERMISSION_KEYS.LEGACY_MANAGE, context) &&
      evaluateRoutePermission(ROUTE_PERMISSION_KEYS.LEGACY_MANAGE_ORG, context)
    )
  }

  return evaluateRoutePermission(resolvedKey, context)
}

function usePermissionContext() {
  const { user } = useUser()
  const clerk = useClerk()
  const { client } = useStatsigClient()
  const uagEnhancements = client.checkGate(RELEASE_GATE_V2_13_0)
  const organizationId = window?.Clerk?.organization?.id
  const previousOrganizationIdRef = useRef(organizationId)

  const [permissionsReady, setPermissionsReady] = useState(() =>
    hasValidStoredPermissions(uagEnhancements, organizationId),
  )

  useEffect(() => {
    if (previousOrganizationIdRef.current === organizationId) {
      return
    }

    previousOrganizationIdRef.current = organizationId
    setPermissionsReady(hasValidStoredPermissions(uagEnhancements, organizationId))
  }, [organizationId, uagEnhancements])

  useEffect(() => {
    if (permissionsReady || !organizationId || !user) {
      return
    }

    if (hasValidStoredPermissions(uagEnhancements, organizationId)) {
      setPermissionsReady(true)
      return
    }

    const pollId = setInterval(() => {
      if (hasValidStoredPermissions(uagEnhancements, organizationId)) {
        setPermissionsReady(true)
      }
    }, PERMISSIONS_POLL_MS)

    const timeoutId = setTimeout(() => {
      if (isOrgSwitchTransitionActive()) {
        return
      }
      if (getImpersonationTargetOrgId()) {
        return
      }
      setPermissionsReady(true)
    }, PERMISSIONS_WAIT_MS)

    return () => {
      clearInterval(pollId)
      clearTimeout(timeoutId)
    }
  }, [permissionsReady, organizationId, user, uagEnhancements])

  const featurePermissions = parseStored('featurePermissions') ?? {}
  const userFeaturePermissions = parseStored('userFeaturePermissions')
  const dynamicConfig = parseStored('dynamicConfig') ?? {}
  const orgUserImpersonationEnabled = parseStored('orgFeaturePermissions')?.user_impersonation

  const permissionContext = useMemo(
    () => ({
      uagEnhancements,
      featurePermissions,
      hasFetchedPermissions: featurePermissions,
      dynamicConfig,
      isAdmin: clerk.user
        ? clerk.session.checkAuthorization({
            role: 'org:admin',
          })
        : false,
      orgUserImpersonationEnabled,
      checkGate: (gate) => client.checkGate(gate),
      hasClerkOrgPermission: (permission) =>
        userOrganizationPermissions(user?.organizationMemberships)?.includes(permission),
    }),
    [
      uagEnhancements,
      featurePermissions,
      dynamicConfig,
      orgUserImpersonationEnabled,
      clerk,
      client,
      user,
    ],
  )

  return {
    permissionsReady: permissionsReady && Boolean(organizationId) && Boolean(user),
    permissionContext,
    uagEnhancements,
    featurePermissions,
    userFeaturePermissions,
  }
}

function RoutePermissionGate({ permission, withHeaderShell = false, children }) {
  const location = useLocation()
  const {
    permissionsReady,
    permissionContext,
    uagEnhancements,
    featurePermissions,
    userFeaturePermissions,
  } = usePermissionContext()
  const orgSwitchActive = isOrgSwitchTransitionActive() && !permissionsReady

  if (orgSwitchActive) {
    return withHeaderShell ? (
      <Header>
        <Loader
          message="Switching organization…"
          data-testid="route-permission-org-switch-loader"
        />
      </Header>
    ) : (
      <Loader message="Switching organization…" data-testid="route-permission-org-switch-loader" />
    )
  }

  if (!permissionsReady) {
    const loaderMessage = getImpersonationTargetOrgId() ? 'Switching organization…' : undefined
    return withHeaderShell ? (
      <Header>
        <Loader message={loaderMessage} />
      </Header>
    ) : (
      <Loader message={loaderMessage} />
    )
  }

  const allowed = hasRoutePermission(permission, permissionContext, location.pathname)

  if (!allowed) {
    if (orgSwitchActive) {
      return withHeaderShell ? (
        <Header>
          <Loader
            message="Switching organization…"
            data-testid="route-permission-org-switch-loader"
          />
        </Header>
      ) : (
        <Loader
          message="Switching organization…"
          data-testid="route-permission-org-switch-loader"
        />
      )
    }

    const deniedContent = (
      <NoPermissionEmptyState
        uagEnhancements={uagEnhancements}
        featurePermissions={featurePermissions}
        userFeaturePermissions={userFeaturePermissions}
        wrapperTestId="route-permission-denied"
        containerTestId="route-permission-empty-state"
        noDataFoundTestId="route-permission-no-data-found"
      />
    )

    return withHeaderShell ? <Header>{deniedContent}</Header> : deniedContent
  }

  return children
}

RoutePermissionGate.PERMISSION_KEYS = ROUTE_PERMISSION_KEYS

export default RoutePermissionGate
